import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import * as utils from './Utils.js';
import { db } from '../firebase.js';

const RSVP = () => {
  const [lastName, setLastName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [matchingReservations, setMatchingReservations] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const findReservationByLastNameAndZip = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setMatchingReservations([]);

    if (!lastName.trim() || !zipCode.trim()) {
      setErrorMessage("Please enter both last name and zip code.");
      return;
    }

    setLoading(true);

    try {
      const reservationsRef = collection(db, "reservations");

      const q = query(
        reservationsRef,
        where("lookup_names", "array-contains", lastName.toLowerCase().replace(/[^a-z]/g, "")),
        where("address.zip", "==", zipCode)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setErrorMessage("No reservation found with that last name and zip code. If this is an error, please double check the zip code we used on your invite. Otherwise, please reach out to Anna or Chandler.");
        return;
      }

      const reservations = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const res = { id: docSnap.id, ...docSnap.data() };

          if (res.guests && res.guests.length > 0) {
            const guestPromises = res.guests.map(async (guestId) => {
              const guestDoc = await getDoc(doc(db, "guests", guestId));
              if (!guestDoc.exists()) return null;
              const g = { id: guestDoc.id, ...guestDoc.data() };

              // omit unnamed guests
              if (g.type === "guest" && !g.name.trim()) return null;
              return g;
            });

            res.guestData = (await Promise.all(guestPromises)).filter(g => g);
          } else {
            res.guestData = [];
          }

          return res;
        })
      );

      if (reservations.length === 1) {
        navigate(`/rsvp/${reservations[0].id}`);
        return;
      }

      setMatchingReservations(reservations);
    } catch (err) {
      console.error(err);
      setErrorMessage("An error occurred while fetching reservations.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReservation = (res) => {
    navigate(`/rsvp/${res.id}`);
  };

  const handleBack = () => {
    setMatchingReservations([]);
    setErrorMessage("");
    setLastName("");
    setZipCode("");
  };

  const showForm = matchingReservations.length === 0;

  return (
    <div className="RSVP">
      <Helmet>
        <title>RSVP</title>
        <meta
          name="description"
          content="Respond to your Wedding invitation!"
        />

        <meta property="og:title" content="RSVP | Chandler & Anna" />
        <meta
          property="og:description"
          content="Respond to your Wedding invitation!"
        />
        <meta
          property="og:image"
          content="https://chandlerandanna.com/og/rsvp.jpg"
        />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:height" content="400" />
        <meta property="og:url" content="https://chandlerandanna.com/rsvp" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="robots" content="index,follow" />
      </Helmet>
      <div className='centered-box wedding-page-block'>
        <div className='wedding-centerpiece'>
          <p className='wedding-page-title'>RSVP</p>
          {showForm && (
            <>
              <p className='wedding-page-info' style={{fontStyle: 'italic', textDecoration: 'underline'}}>Please RSVP by March 15.</p>
              <p className='wedding-page-info'>Enter your last name and zip code to find and respond to your invitation.</p>
            </>
          )}

          {showForm && (
            <div className='wedding-form-container'>
              <form className='wedding-form' onSubmit={findReservationByLastNameAndZip}>
                {errorMessage && (
                  <div className="alert alert-primary-wedding" role="alert">
                    {errorMessage}
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="inputName" className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="inputName"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="inputZipcode" className="form-label">Zip Code</label>
                  <input
                    style={{ maxWidth: '120px' }}
                    type="text"
                    className="form-control"
                    id="inputZipcode"
                    aria-describedby="zipcodeHelp"
                    placeholder="Zip Code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                  />
                  <small id="zipcodeHelp" className="form-text text-muted">
                    Use the zip code your invitation was mailed to.
                  </small>
                </div>

                <button className="btn btn-navyblue" type="submit" disabled={loading}>
                  {loading ? "Searching..." : "Find invitation"}
                </button>
              </form>
            </div>
          )}

          {matchingReservations.length > 1 && (
            <div className='mt-3 mx-3 multiple-reservation-container'>
              <div className="alert alert-primary-wedding" role="alert">
                Multiple reservations found. Please choose your invitation.
              </div>

              <button className="btn btn-outline-navyblue" onClick={handleBack}>
                Back to Search
              </button>

              <div className="mt-3">
                {matchingReservations.map((res) => (
                  <div key={res.id} className="mb-4 p-3 border rounded">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p style={{ fontSize: '18px', fontWeight: '700' }}>{res.name}</p>
                      <button
                        className="btn btn-navyblue"
                        onClick={() => handleSelectReservation(res)}
                      >
                        RSVP
                      </button>
                    </div>

                    {res.guestData && res.guestData.length > 0 && (
                      <div className="ms-4">
                        {res.guestData.map(g => (
                          <p key={g.id} className="mb-1">
                            {g.name}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='centered-box'>
        { utils.displayCheckerboard(20, 42, ['green', 'ivory']) }
      </div>
    </div>
  );
};

export default RSVP;
